import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule,
} from 'vuex-module-decorators';

import store from '@/store';
import { ErrorAlertModule } from '@/store/modules/errors';
import * as api from '@/api/recendStatement';
import { CommonObject, IAsyncState } from '@/utils/types';
import {
  PayloadRecendSave,
  PayloadRecendSendPackage,
} from '@/api/recendStatement';
import { emptyToNull, errorHandler } from '@/utils/common';
import { ITrustee } from '@/types/services';
import deepmerge from 'deepmerge';
import { AddStatementDraftResp } from '@/api/additionalStatement';

@Module({ dynamic: true, store, name: 'RecendStatement', namespaced: true })
class RecendStatement extends VuexModule {
  public addStatement: IAsyncState<CommonObject> = {
    entity: null,
    loading: false,
    loaded: false,
  };
  public saveState: IAsyncState = {
    loading: false,
    loaded: false,
  };
  public statementXmlBase64: IAsyncState<string> = {
    entity: null,
    loading: false,
    loaded: false,
  };
  public sendPackageState: IAsyncState = {
    loading: false,
    loaded: false,
  };
  public deleteStatementLinkState: IAsyncState = {
    loading: false,
    loaded: false,
  };
  public saveDraftState: IAsyncState = {
    loading: false,
    loaded: false,
  };

  @Mutation
  private pendingGetAddStatementById() {
    ErrorAlertModule.setErrorAlert('');
    this.addStatement.entity = null;
    this.addStatement.loading = true;
    this.addStatement.fetching = true;
    this.addStatement.loaded = false;
  }

  @Mutation
  private fulfilledGetAddStatementById(addStatement: CommonObject) {
    this.addStatement.entity = addStatement;
    this.addStatement.loading = false;
    this.addStatement.fetching = false;
    this.addStatement.loaded = true;
  }

  @Mutation
  private rejectedGetAddStatementById(error: unknown) {
    errorHandler(error);
    this.addStatement.loading = false;
    this.addStatement.fetching = false;
  }

  @Mutation
  private pendingSave() {
    ErrorAlertModule.setErrorAlert('');
    this.saveState.loading = true;
    this.saveState.loaded = false;
  }

  @Mutation
  private fulfilledSave() {
    this.saveState.loading = false;
    this.saveState.loaded = true;
  }

  @Mutation
  private rejectedSave(error: unknown) {
    errorHandler(error);
    this.saveState.loading = false;
  }

  @Mutation
  private pendingGetStatementXmlBase64() {
    ErrorAlertModule.setErrorAlert('');
    this.statementXmlBase64.entity = null;
    this.statementXmlBase64.loading = true;
    this.statementXmlBase64.loaded = false;
  }

  @Mutation
  private fulfilledGetStatementXmlBase64(statementXmlBase64: string) {
    this.statementXmlBase64.entity = statementXmlBase64;
    this.statementXmlBase64.loading = false;
    this.statementXmlBase64.loaded = true;
  }

  @Mutation
  private rejectedGetStatementXmlBase64(error: unknown) {
    errorHandler(error);
    this.statementXmlBase64.loading = false;
  }

  @Mutation
  private pendingSendPackage() {
    ErrorAlertModule.setErrorAlert('');
    this.sendPackageState.loading = true;
    this.sendPackageState.loaded = false;
  }

  @Mutation
  private fulfilledSendPackage() {
    this.sendPackageState.loading = false;
    this.sendPackageState.loaded = true;
  }

  @Mutation
  private rejectedSendPackage(error: unknown) {
    errorHandler(error);
    this.sendPackageState.loading = false;
  }

  @Mutation
  private pendingDeleteStatementLink() {
    ErrorAlertModule.setErrorAlert('');
    this.deleteStatementLinkState.loading = true;
    this.deleteStatementLinkState.loaded = false;
  }

  @Mutation
  private fulfilledDeleteStatementLink() {
    this.deleteStatementLinkState.loading = false;
    this.deleteStatementLinkState.loaded = true;
  }

  @Mutation
  private rejectedDeleteStatementLink(error: unknown) {
    errorHandler(error);
    this.deleteStatementLinkState.loading = false;
  }

  @Action({ rawError: true })
  async getAddStatementById(id: string) {
    this.pendingGetAddStatementById();

    try {
      const statement = await api.recendGetAddStatementById(id);
      this.fulfilledGetAddStatementById(statement);
    } catch (error) {
      this.rejectedGetAddStatementById(error);
    }
  }

  @Action({ rawError: true })
  async save(payload: PayloadRecendSave) {
    this.pendingSave();

    try {
      await api.recendSave(payload);
      this.fulfilledSave();
    } catch (error) {
      this.rejectedSave(error);
    }
  }

  /**
   *  При вызове также происходит генерация xml
   * */
  @Action({ rawError: true })
  async getStatementXmlBase64(id: string) {
    this.pendingGetStatementXmlBase64();

    try {
      const statementXmlBase64Response = await api.recendStatementXmlBase64(id);
      this.fulfilledGetStatementXmlBase64(statementXmlBase64Response);
    } catch (error) {
      this.rejectedGetStatementXmlBase64(error);

      throw error;
    }
  }

  @Action({ rawError: true })
  async sendPackage(payload: PayloadRecendSendPackage) {
    this.pendingSendPackage();

    try {
      await api.recendSendPackage(payload);
      this.fulfilledSendPackage();
    } catch (error) {
      this.rejectedSendPackage(error);

      throw error;
    }
  }

  /**
   * @param id - id recend заявления
   * */
  @Action({ rawError: true })
  async deleteStatementLink(id: string) {
    this.pendingDeleteStatementLink();

    try {
      await api.recendDeleteStatementLink(id);
      this.fulfilledDeleteStatementLink();
    } catch (error) {
      this.rejectedDeleteStatementLink(error);
    }
  }

  @Mutation
  private pendingSaveDraft() {
    ErrorAlertModule.setErrorAlert('');
    this.saveDraftState.loading = true;
    this.saveDraftState.loaded = false;
  }

  @Mutation
  private fulfilledSaveDraft() {
    this.saveDraftState.loading = false;
    this.saveDraftState.loaded = true;
  }

  @Mutation
  private rejectedSaveDraft(error: unknown) {
    errorHandler(error);
    this.saveDraftState.loading = false;
  }

  @Action({ rawError: true })
  async saveDraft({
    initialAddSt,
    trustee,
    role,
  }: {
    initialAddSt: AddStatementDraftResp;
    trustee: ITrustee;
    role: string;
  }) {
    let draft: CommonObject = initialAddSt;

    if (trustee) {
      draft = deepmerge(initialAddSt, {
        applicant: {
          [role]: {
            trusteeInfo: trustee ,
          },
        },
      });

      if (!draft.applicant[role].trusteeInfo.documents.statusDocument?.name) {
        delete draft.applicant[role].trusteeInfo.documents.statusDocument;
      }
    }

    this.pendingSaveDraft();

    try {
      await api.recendSaveDraft(emptyToNull(draft));
      this.fulfilledSaveDraft();
    } catch (error) {
      this.rejectedSaveDraft(error);

      throw error;
    }
  }
}

export const RecendStatementModule = getModule(RecendStatement);
